"use client";

import { useState } from "react";

import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslations } from "next-intl";

import { titleToId } from "@/shared/lib/text";
import { Typography } from "@/shared/ui";

import { CaptionCards, CardCaptionContent } from "./CaptionCards";
import { weekColors } from "./RecommendationCard";
import { WakeSchedule } from "./WakeSchedule";

const ages = [
    "0-3 months",
    "3-4 months",
    "5-6 months",
    "7-12 months",
    "12-18 months",
    "18-24 months",
    "2 years",
    "3 years",
    "4 years",
    "5 years",
];

export const WakeWindows = ({
    age,
    ageText,
    isStandalone,
}: {
    age: string;
    ageText: string;
    isStandalone?: boolean;
}) => {
    const t = useTranslations("WakeWindows");
    const t_r = useTranslations("Recommendation");
    const [isExpanded, setIsExpanded] = useState(true);
    const [selectedAge, setSelectedAge] = useState(age);

    const captions = [
        {
            color: weekColors[`Week1`],
            text: `${t_r("week")} 1`,
            week: 1,
        } as CardCaptionContent,
    ];

    return (
        <div className="mb-16 scroll-mt-24" id={titleToId(t("title"))}>
            <div className="space-y-6">
                <div className="rounded-xl p-8 relative bg-white" key="recommendation_wake_windows">
                    {!isStandalone && (
                        <>
                            <CaptionCards captions={captions} />
                            <div
                                className="flex items-center cursor-pointer mb-4"
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                <Typography className="font-semibold text-secondary-300 flex-1" variant="title3">
                                    {t("title")}
                                </Typography>
                                {isExpanded ? (
                                    <ChevronUp className="w-8 h-8 text-primary-100 ml-2" />
                                ) : (
                                    <ChevronDown className="w-8 h-8 text-primary-100 ml-2" />
                                )}
                            </div>
                        </>
                    )}
                    {isStandalone && (
                        <Typography className="font-semibold text-secondary-300 flex-1" variant="title3">
                            {t("title")}
                        </Typography>
                    )}
                    {isExpanded && (
                        <>
                            <div className="space-y-4 mb-6">
                                {isStandalone && (
                                    <select
                                        className="px-4 py-2 rounded-lg border border-black bg-white text-black focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-transparent"
                                        onChange={(e) => setSelectedAge(e.target.value)}
                                        value={selectedAge}
                                    >
                                        {ages.map((ageA) => (
                                            <option key={ageA} value={ageA}>
                                                {ageA}
                                            </option>
                                        ))}
                                    </select>
                                )}
                                <Typography variant="body">{t("intro")}</Typography>
                                <ul className="list-disc pl-6">
                                    {t(selectedAge)
                                        .split("\n")
                                        .map((item: string, index: number) => {
                                            const lvlItems = item.split("\t");
                                            return (
                                                <li key={index}>
                                                    <Typography variant="body">{lvlItems[0]}</Typography>
                                                    {lvlItems.length > 1 && (
                                                        <ul>
                                                            {lvlItems.map((subitem: string, subindex: number) => (
                                                                <li key={subindex}>
                                                                    <Typography className="ml-4" variant="body">
                                                                        {subitem}
                                                                    </Typography>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </li>
                                            );
                                        })}
                                </ul>
                            </div>

                            <WakeSchedule age={selectedAge} ageText={ageText} />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
